import {
  formatTimeAgo,
  formatUsdAmountDisplayCents,
} from './function';
import { ensureAtMentionInText, stripUsernameAt } from './usernameFormat';
import { ROLE } from '../constants/common';
import {
  ACTIVITY_TITLE_MAP,
  TITLES_WITH_INLINE_MENTIONS,
} from '../constants/activity';

/**
 * Notification id for PATCH /viewed (Mongo _id or API `id`).
 * Audience/tags batch rows often expose `id` without `_id`.
 * @param {object} raw
 * @returns {string|null}
 */
export function getActivityNotificationApiId(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const candidates = [
    raw._id,
    raw.id,
    raw.notificationId,
    raw.notification_id,
  ];
  for (const c of candidates) {
    if (c == null || c === '') continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return null;
}

/**
 * Grouped activity rows (batch tiles) expose member notification ids here when `_id` is null.
 * @param {object} raw
 * @returns {string[]}
 */
export function getActivityNotificationUnreadIds(raw) {
  if (!raw || typeof raw !== 'object') return [];
  const arr = raw.unreadIds ?? raw.unread_ids ?? raw.unreadIDs;
  if (!Array.isArray(arr)) return [];
  return arr
    .map((id) => (id == null || id === '' ? '' : String(id).trim()))
    .filter(Boolean);
}

/**
 * PATCH /viewed ids for one activity row (single _id or grouped unreadIds).
 * @param {object} raw
 * @returns {string[]}
 */
export function resolveActivityNotificationViewIds(raw) {
  if (!raw || typeof raw !== 'object') return [];
  const single = getActivityNotificationApiId(raw);
  if (single) return [single];
  return getActivityNotificationUnreadIds(raw);
}

/**
 * API may omit or null `_id`; use a stable key for list keys and Redux dedupe.
 * @param {object} raw
 * @returns {string}
 */
export function getActivityNotificationStableId(raw) {
  if (!raw) return '';
  const docId = getActivityNotificationApiId(raw);
  if (docId) return docId;
  const ts = raw.created_at ?? raw.createdAt ?? '';
  const from = raw.from_user_id ?? '';
  const ev = raw.eventType ?? raw.type ?? '';
  const key = `${from}:${ts}:${ev}`;
  return key === '::' ? `synthetic:${Date.now()}` : `synthetic:${key}`;
}

/** Same explicit-flag rules as mapCreatorNotificationToActivityItem (`isUnread`). */
export function rawActivityNotificationLooksUnread(raw) {
  if (!raw || typeof raw !== 'object') return false;
  return (
    raw.isRead === false ||
    raw.isViewed === false ||
    raw.read === false ||
    raw.viewed === false ||
    raw.is_read === false ||
    raw.is_viewed === false
  );
}

export function countExplicitUnreadInRawItems(items) {
  if (!Array.isArray(items)) return 0;
  let n = 0;
  for (const raw of items) {
    if (rawActivityNotificationLooksUnread(raw)) n += 1;
  }
  return n;
}

const READ_ACTIVITY_ROW_FLAGS = {
  isRead: true,
  isViewed: true,
  read: true,
  viewed: true,
  is_read: true,
  is_viewed: true,
};

/** Collect stable ids already marked read in any grouped variant for a tab. */
export function collectReadActivityIdsFromTab(byGrouped) {
  const readIds = new Set();
  if (!byGrouped) return readIds;
  for (const slice of Object.values(byGrouped)) {
    for (const row of slice?.items || []) {
      if (rawActivityNotificationLooksUnread(row)) continue;
      const id = getActivityNotificationStableId(row);
      if (id) readIds.add(id);
    }
  }
  return readIds;
}

/**
 * After PATCH viewed, API refetches may still return unread flags briefly.
 * Keep local read state so badges do not flash back to 1 until the server catches up.
 */
export function applyLocallyReadStateToActivityItems(readIds, apiItems) {
  if (!readIds?.size || !Array.isArray(apiItems)) return apiItems;
  return apiItems.map((row) => {
    const id = getActivityNotificationStableId(row);
    if (!id || !readIds.has(id) || !rawActivityNotificationLooksUnread(row)) {
      return row;
    }
    return { ...row, ...READ_ACTIVITY_ROW_FLAGS };
  });
}

/**
 * Unread for badges: count pink-dot rows in loaded items. When the API total disagrees
 * with visible dots (e.g. count 2, one dot), cap at the API value. When nothing looks
 * unread locally, return 0 — do not trust a stale API total after rows were marked viewed.
 */
export function reconcileActivitySliceUnreadCount(slice) {
  if (!slice) return 0;
  const dots = countExplicitUnreadInRawItems(slice.items);
  if (dots === 0) return 0;
  const apiUnread =
    typeof slice.unreadCount === 'number' ? slice.unreadCount : 0;
  return Math.min(apiUnread, dots);
}

/**
 * Sum unread across activity tabs for the footer heart badge (excludes archived).
 * @param {object} byTab - state.activity.byTab
 * @param {string[]} tabIds
 * @returns {{ sum: number, anyFetched: boolean }}
 */
export function getActivityFooterUnreadBadgeSource(byTab, tabIds) {
  let sum = 0;
  let anyFetched = false;
  for (const tabId of tabIds) {
    if (tabId === 'archived') continue;
    const byGrouped = byTab?.[tabId]?.byGrouped;
    if (!byGrouped) continue;
    if (Object.values(byGrouped).some((s) => s?.fetched)) {
      anyFetched = true;
    }
    sum += getActivityTabUnreadCount(tabId, byTab);
  }
  return { sum, anyFetched };
}

function roundMoney(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return n;
  return Math.round(x * 100) / 100;
}

/** API may send `discounted_price: "free"` for trial / discount resubscribe promos. */
function isPromoDiscountedPriceFreeToken(value) {
  if (value == null || value === '') return false;
  return typeof value === 'string' && value.trim().toLowerCase() === 'free';
}

/** Long decimal amounts in API copy → readable dollars in strings */
export function prettifyMoneyInText(text) {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/\$(\d+\.\d{6,})/g, (_, num) => `$${roundMoney(num)}`);
}

/**
 * API may send the server default profile as a short path or a full CDN URL.
 * Treat those as "no photo" so Purchases & Tips rows show the designed tip icon
 * (see ActivityList: img vs getActivityIcon).
 */
function isServerDefaultAvatar(fromAvatar) {
  if (fromAvatar == null || fromAvatar === '') return true;
  if (typeof fromAvatar !== 'string') return false;
  const s = fromAvatar.trim().toLowerCase();
  if (!s) return true;
  if (s === 'default/default-user.jpeg') return true;
  if (s.includes('default-user') || s.includes('default_user')) return true;
  return false;
}

function resolveAvatar(fromAvatar) {
  if (isServerDefaultAvatar(fromAvatar)) {
    return null;
  }
  return fromAvatar;
}

function resolveCreatorId(raw = {}, nestedFromUser = null) {
  return (
    raw.creator_id ??
    raw.creatorId ??
    raw.from_user_id ??
    raw.fromUserId ??
    raw.other_user_id ??
    raw.otherUserId ??
    nestedFromUser?.id ??
    nestedFromUser?._id ??
    null
  );
}

/**
 * Mongo user id of the fan/subscriber in a creator activity row.
 * Prefer action-initiator fields (from_user_id) over creator_id on the payload.
 * @param {object} item Mapped activity list item (with `_raw`)
 * @returns {string|null}
 */
export function resolveActivitySubscriberUserId(item) {
  const raw = item?._raw;
  if (!raw || typeof raw !== 'object') return null;

  let meta = raw.meta;
  if (typeof meta === 'string') {
    try {
      meta = JSON.parse(meta);
    } catch {
      meta = null;
    }
  }

  const nestedFromUser =
    raw.from_user ||
    raw.fromUser ||
    raw.from ||
    raw.other_user ||
    raw.otherUser ||
    null;

  const candidates = [
    raw.from_user_id,
    raw.fromUserId,
    raw.user_id,
    raw.userId,
    raw.subscriber_id,
    raw.subscriberId,
    raw.buyer_id,
    raw.buyerId,
    nestedFromUser?.id,
    nestedFromUser?._id,
    raw.other_user_id,
    raw.otherUserId,
    meta?.from_user_id,
    meta?.user_id,
    meta?.userId,
  ];

  for (const value of candidates) {
    if (value == null || String(value).trim() === '') continue;
    return String(value).trim();
  }
  return null;
}

function mapEventToListType(eventType, apiType, raw = {}) {
  // Refunds may keep `type: message_tip` (or `tip`) while `eventType` is `refund`.
  // Detect refund before tip_id heuristics so icon + Purchases & Tips badges match.
  if (eventType === 'refund' || apiType === 'refund') return 'refund';

  // Subscriber active notifications may not include a normalized `type/eventType`.
  // Infer purchases & tips from tip fields:
  // - tip_id + message => message_tip
  // - only tip_id     => tip
  const hasTipId = raw?.tip_id != null && String(raw.tip_id).trim() !== '';
  const hasMessage = typeof raw?.message === 'string' && raw.message.trim() !== '';
  if (hasTipId && hasMessage) return 'message_tip';
  if (hasTipId) return 'tip';

  // Explicit mapping for subscriber "Purchases & Tips" payloads.
  // Keep this before generic eventType switches so `tip_sent` can still
  // distinguish `message_tip` from regular `tip`.
  if (apiType === 'call_credits' && eventType === 'call_credits') return 'credits';
  if (apiType === 'purchase_content' && eventType === 'bundle_unlocked') return 'bundle';
  if (apiType === 'purchase_content') return 'unlock';
  if (apiType === 'message_tip' && eventType === 'tip_sent') return 'message_tip';
  if (apiType === 'tip' && eventType === 'tip_sent') return 'tip';
  if (apiType === 'subscription_batch' && eventType === 'new_subscribers_batch') return 'new_subscribers';
  if (apiType === 'feed_content_sold' && eventType === 'feed_content_sold') return 'feed_content_sold';
  if (apiType === 'feed_bundle_sold' && eventType === 'feed_bundle_sold') return 'feed_bundle_sold';
  if (apiType === 'message_bundle_sold' && eventType === 'message_bundle_sold') return 'message_bundle_sold';
  if (apiType === 'followers_batch' && eventType === 'new_followers_batch') return 'new_followers';
  if (apiType === 'creator_priority_messages' && eventType === 'unread_priority_messages_banner') {
    return 'priority_messages';
  }
  if (apiType === 'trending' && eventType === 'trending_profile') return 'trending';
  if (apiType === 'creator_trending' && eventType === 'trending_profile_started') {
    return 'trending';
  }


  if (raw.content_id != null && (raw.content_type || raw.type_file)) {
    return 'bundles';
  }
  if (apiType === 'tag') return 'tag';
  switch (eventType) {
    case 'payout_processed':
    case 'payout_pending':
    case 'payout_failed':
      return eventType;
    case 'tagged_in_post':
      return 'tag';
    case 'tip_received':
    case 'tip_sent':
      return 'tip';
    case 'message_content_sold':
      return 'message_content_sold';
    case 'new_subscriber':
      return 'subscription';
    case 'discount_active':
    case 'discount_ended':
    case 'discount_expired':
    case 'discount_canceled':
    case 'discount_cancelled':
      return 'discounted';
    case 'promo_trial_renewal':
      return 'promo_trial_renewal';
    case 'promo_discount_renewal':
      return 'promo_discount_renewal';
    case 'promo_trial_resubscribe':
      return 'promo_trial_resubscribe';
    case 'promo_discount_resubscribe':
      return 'promo_discount_resubscribe';
    case 'new_follower':
      return 'new_follower';
    default:
      if (apiType === 'message_tip' || apiType === 'tip') return 'tip';
      if (apiType === 'subscription' || raw.notificationType === 'subscription_success') {
        return 'subscription';
      }
      if (isDiscountActivityEvent(eventType, apiType, raw)) {
        return 'discounted';
      }
      return 'credits';
  }
}

function isDiscountActivityEvent(eventType, apiType, raw = {}) {
  const ev = String(eventType || '').toLowerCase();
  const ty = String(apiType || '').toLowerCase();
  if (ev.includes('discount') || ty.includes('discount')) return true;
  const title = String(raw.title || '').toLowerCase();
  return title.includes('discount');
}

/**
 * Map creator notification API row → ActivityList item shape
 * @param {object} raw
 */
export function mapCreatorNotificationToActivityItem(raw) {
  if (!raw) return null;

  const id = getActivityNotificationStableId(raw);
  if (!id) return null;

  const created = raw.createdAt ? new Date(raw.createdAt) : null;
  const timeAgo =
    created && !Number.isNaN(created.getTime())
      ? formatTimeAgo(created, { addSuffix: true })
      : '';

  const listType = mapEventToListType(
    raw.eventType ?? raw.event_type,
    raw.type,
    raw
  );
  const eventTypeNormEarly = raw.eventType ?? raw.event_type ?? '';
  const isNewSubscribersBatch =
    raw.type === 'subscription_batch' &&
    eventTypeNormEarly === 'new_subscribers_batch';
  const subscriberTypeNorm = String(
    raw.subscriber_type ?? raw.subscriberType ?? ''
  ).toLowerCase();
  const isExpiredPromoResubscribe =
    subscriberTypeNorm === 'expired' &&
    (raw.eventType === 'promo_discount_resubscribe' ||
      raw.eventType === 'promo_trial_resubscribe');

  let amount =
    raw.amount != null && Number.isFinite(Number(raw.amount))
      ? roundMoney(raw.amount)
      : undefined;

  let promoResubscribeIsFree = false;
  const discountedPriceRaw =
    raw.discounted_price ?? raw.discountedPrice ?? null;
  if (isExpiredPromoResubscribe && discountedPriceRaw != null) {
    if (isPromoDiscountedPriceFreeToken(discountedPriceRaw)) {
      promoResubscribeIsFree = true;
      amount = undefined;
    } else if (Number.isFinite(Number(discountedPriceRaw))) {
      amount = roundMoney(discountedPriceRaw);
    }
  } else if (
    (eventTypeNormEarly === 'discount_active' || listType === 'discounted') &&
    discountedPriceRaw != null &&
    Number.isFinite(Number(discountedPriceRaw))
  ) {
    amount = roundMoney(discountedPriceRaw);
  }

  // Priority-message API returns nested creator/user info:
  //   { from_user: { username, profile_picture }, message, amount, days_left, isViewed }
  // Some endpoints use alternative keys; keep this mapping defensive.
  const nestedFromUser =
    raw.from_user ||
    raw.fromUser ||
    raw.from ||
    raw.other_user ||
    raw.otherUser ||
    raw.other ||
    raw.creator ||
    raw.creator_user ||
    raw.creatorUser ||
    null;

  const username =
    raw.from_username ??
    raw.fromUsername ??
    raw.username ??
    raw.creator_username ??
    raw.creatorUsername ??
    nestedFromUser?.username ??
    nestedFromUser?.user_name ??
    nestedFromUser?.userName ??
    nestedFromUser?.handle ??
    raw.from_user?.username ??
    raw.from_user?.user_name ??
    raw.other_user?.username ??
    raw.other_user?.user_name ??
    null;

  let title = raw.title || '';
  let description =
    raw.message != null ? prettifyMoneyInText(String(raw.message)) : '';

  const usernameBare = username ? stripUsernameAt(username) : '';

  // Subscription notifications may come with placeholder titles (or no title)
  // depending on the backend integration. Prefer the exact designed copy.
  const apiTypeForMapping = raw.type || raw.apiType || '';
  const notificationTypeLower =
    typeof raw.notificationType === 'string'
      ? raw.notificationType.toLowerCase()
      : '';
  const isSuccessfulAutoRenewal =
    notificationTypeLower.includes('renew') &&
    notificationTypeLower.includes('success') &&
    (apiTypeForMapping === 'subscription' || notificationTypeLower.includes('subscription'));
  const isSuccessfulSubscription = notificationTypeLower === 'subscription_success';

  const imageCountNum = (() => {
    const v = raw.image_count ?? raw.imageCount;
    const n = v == null ? NaN : Number(v);
    return Number.isFinite(n) ? n : 0;
  })();

  const videoCountNum = (() => {
    const v = raw.video_count ?? raw.videoCount;
    const n = v == null ? NaN : Number(v);
    return Number.isFinite(n) ? n : 0;
  })();

  // "One-off" post means this notification represents exactly one piece of media
  // (either 1 image or 1 video).
  const isOneOffMediaPost =
    imageCountNum + videoCountNum === 1 &&
    (imageCountNum === 1 || videoCountNum === 1);

  if (!title && raw.content_id != null && username) {
    const ct = raw.content_type || raw.type_file || 'content';
    const ctLower = String(ct).toLowerCase();

    // For one-off new posts, prefer the explicit "Posted a new video/picture"
    // copy so it doesn't read like a generic batch ("New image/video content").
    if (isOneOffMediaPost) {
      if (ctLower.includes('video') || videoCountNum === 1) {
        title = 'Posted a new video';
        description = 'Posted a new video';
      } else if (ctLower.includes('image') || imageCountNum === 1) {
        title = 'Posted a new picture';
        description = 'Posted a new picture';
      } else {
        title = 'Posted a new post';
        description = 'Posted a new post';
      }
    } else {
      title = 'New post';
      description = `New ${ct} content`;
    }
  }

  if (isSuccessfulSubscription) {
    title = 'Successfully Subscribed 🎊';
  } else if (isSuccessfulAutoRenewal) {
    title = 'Successful Auto-Renewal';
  }

  const rawPrice = raw.price ?? raw.amount;
  const price =
    rawPrice != null && Number.isFinite(Number(rawPrice))
      ? roundMoney(rawPrice)
      : undefined;

  const monetizationNorm = String(
    raw.monetization_type ?? raw.monetizationType ?? ''
  ).toLowerCase();
  const isLockedContent =
    raw.lock_content === true ||
    raw.lockContent === true ||
    String(raw.lock_content).toLowerCase() === 'true' ||
    String(raw.lockContent).toLowerCase() === 'true';
  // Subscriber "new posts" rows can be paid PPV content even when the creator account
  // itself is a subscription creator (creator_rol = 1). Prefer the content lock/price
  // signal over the creator account tier so we render the PPV badge instead of "Total $...".
  const isPPV =
    monetizationNorm === 'ppv' ||
    (isLockedContent &&
      ((price != null && Number(price) > 0) ||
        (amount != null && Number(amount) > 0)));

  const postBatchRaw = raw.post_batch_count ?? raw.postBatchCount;
  const postBatchCount =
    postBatchRaw != null && Number.isFinite(Number(postBatchRaw))
      ? Number(postBatchRaw)
      : null;

  // Priority-message UI expects these fields for the pink badge.
  // If this isn't a priority-message row, they stay null and `getBadge()` ignores them.
  const rawDaysLeft = raw.days_left ?? raw.daysLeft ?? null;
  const daysLeftNum =
    rawDaysLeft == null || rawDaysLeft === ''
      ? null
      : (() => {
        const n = Number(rawDaysLeft);
        return Number.isFinite(n) ? n : null;
      })();

  const sentTip =
    raw.sentTip != null
      ? raw.sentTip
      : listType === 'promo_discount_resubscribe' ||
        listType === 'promo_trial_resubscribe'
        ? null
        : amount != null
          ? amount
          : null;

  const tipId = raw.tip_id ?? raw.tipId ?? raw.tipID ?? null;

  const isExtended = Boolean(
    raw.is_extended ??
    raw.isExtended ??
    raw.tip_extended ??
    raw.tipExtended ??
    false
  );

  // Unread if any explicit unread flag (batch/audience rows vary by field name).
  const isUnread =
    raw.isRead === false ||
    raw.isViewed === false ||
    raw.read === false ||
    raw.viewed === false ||
    raw.is_read === false ||
    raw.is_viewed === false;
  const isRead = !isUnread;
  const viewedAt = raw.viewedAt ?? raw.viewed_at ?? null;

  const apiNotificationId = getActivityNotificationApiId(raw);
  const unreadApiIds = getActivityNotificationUnreadIds(raw);
  const creatorId = resolveCreatorId(raw, nestedFromUser);

  const eventTypeNorm = eventTypeNormEarly || null;
  const creatorPriceRenewalRaw =
    raw.creator_price ?? raw.creatorPrice ?? null;
  let promoRenewalMonthlyPriceFormatted = null;
  if (
    (eventTypeNorm === 'promo_discount_renewal' ||
      eventTypeNorm === 'promo_trial_renewal') &&
    creatorPriceRenewalRaw != null &&
    String(creatorPriceRenewalRaw).trim() !== ''
  ) {
    const priceNum = Number(creatorPriceRenewalRaw);
    if (Number.isFinite(priceNum)) {
      promoRenewalMonthlyPriceFormatted =
        formatUsdAmountDisplayCents(priceNum);
    }
  }

  if (usernameBare) {
    if (description) {
      description = ensureAtMentionInText(description, usernameBare);
    }
    if (title) {
      title = ensureAtMentionInText(title, usernameBare);
    }
  }

  const pillTextRaw = raw.pillText ? String(raw.pillText) : '';
  const pillTextNormalized =
    pillTextRaw && usernameBare
      ? ensureAtMentionInText(pillTextRaw, usernameBare)
      : pillTextRaw;

  return {
    id,
    title,
    username,
    description,
    timeAgo,
    type: listType,
    amount,
    promoResubscribeIsFree,
    pillText: pillTextNormalized ? prettifyMoneyInText(pillTextNormalized) : null,
    highlight: isUnread,
    avatar: resolveAvatar(
      raw.from_avatar ||
      raw.creator_avatar ||
      raw.fromAvatar ||
      raw.creatorAvatar ||
      raw.profile_picture ||
      raw.profilePicture ||
      raw.avatar ||
      raw.other_user?.profile_picture ||
      raw.other_user?.profilePicture ||
      raw.other_user?.profile_photo ||
      nestedFromUser?.profile_picture ||
      nestedFromUser?.profile_photo ||
      nestedFromUser?.profile_pic ||
      nestedFromUser?.profilePhoto ||
      nestedFromUser?.profilePicture ||
      nestedFromUser?.profilePic ||
      nestedFromUser?.avatar ||
      nestedFromUser?.picture_url ||
      nestedFromUser?.avatar_url ||
      null
    ),
    deepLink:
      raw.deepLink ||
      raw.deep_link ||
      raw.link ||
      raw.url ||
      raw.path ||
      null,
    creatorId,
    isPPV,
    price,
    postBatchCount,
    // Only used by ActivityList's priority badge rendering.
    sentTip,
    daysLeft: daysLeftNum,
    tipId,
    isExtended,
    // Read/archive fields returned to ActivityList consumers.
    isRead,
    viewedAt,
    apiNotificationId,
    unreadApiIds,
    promoRenewalMonthlyPriceFormatted,
    _raw: raw,
    forceActivityIcon: isSuccessfulAutoRenewal || isNewSubscribersBatch,
    iconType: isSuccessfulAutoRenewal ? 'auto_renewal' : listType,
    showIconWhenAvatarMissing: isSuccessfulSubscription || isSuccessfulAutoRenewal,
  };
}

export function mapCreatorNotifications(items) {
  if (!Array.isArray(items)) return [];
  return items.map(mapCreatorNotificationToActivityItem).filter(Boolean);
}

/**
 * Whether an activity list slice has another page (active or archived pagination).
 * @param {object|null|undefined} pagination
 * @returns {boolean}
 */
export function activityPaginationHasMore(pagination) {
  if (!pagination) return false;
  if (typeof pagination.has_next === 'boolean') return pagination.has_next;
  const currentPage = pagination.page ?? 1;
  const totalPages = Math.max(1, pagination.total_pages || 1);
  return currentPage < totalPages;
}

/**
 * Keep archived pagination aligned with the 1-based page we requested (limit/page query params).
 * @param {object|null|undefined} pagination
 * @param {{ page: number, limit: number }} request
 * @returns {object}
 */
export function normalizeArchivedActivityPagination(pagination, { page, limit }) {
  const resolvedLimit = pagination?.limit ?? limit;
  const totalCount =
    pagination?.total_count ??
    pagination?.total ??
    pagination?.totalCount ??
    0;

  return {
    ...(pagination || {}),
    page,
    limit: resolvedLimit,
    total_count: totalCount,
    total_pages:
      pagination?.total_pages ??
      Math.max(1, Math.ceil(totalCount / resolvedLimit) || 1),
  };
}

/**
 * Unread count for filter bar + footer: dedupe unread rows across grouped/non-grouped
 * variants that were fetched for this tab.
 * @param {string} tabId
 * @param {object} byTab - state.activity.byTab
 * @returns {number}
 */
export function getActivityTabUnreadCount(tabId, byTab) {
  if (tabId === 'all') return 0;
  const byGrouped = byTab?.[tabId]?.byGrouped;
  if (!byGrouped) return 0;

  const falseSlice = byGrouped['false'];
  const trueSlice = byGrouped['true'];
  const slicesToScan = (() => {
    if (trueSlice?.fetched) return [trueSlice];
    if (falseSlice?.fetched) return [falseSlice];
    return [];
  })();

  const seen = new Set();
  let unread = 0;
  for (const slice of slicesToScan) {
    for (const raw of slice.items || []) {
      if (!rawActivityNotificationLooksUnread(raw)) continue;
      const stableId = getActivityNotificationStableId(raw);
      if (!stableId || seen.has(stableId)) continue;
      seen.add(stableId);
      unread += 1;
    }
  }
  return unread;
}

export function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function getActivityIcon(type) {
  switch (type) {
    case 'payout_processed':
      return <i className="fa-solid fa-money-check-dollar"></i>;
    case 'payout_pending':
      return <i className="fa-solid fa-clock"></i>;
    case 'payout_failed':
      return <i className="fa-solid fa-triangle-exclamation"></i>;
    case 'auto_renewal':
      return <i className="fa-solid fa-arrows-rotate"></i>;
    case 'tag':
      return <i className="fa-solid fa-user-tag"></i>;
    case 'tip':
    case 'message_tip':
      return <i className="fa-solid fa-hand-holding-dollar"></i>;
    case 'ppv':
      return <i className="fa-solid fa-lock"></i>;
    case 'unlock':
      return <i className="fa-solid fa-unlock"></i>;
    case 'bundle':
    case 'bundles':
      return <i className="fa-solid fa-photo-film"></i>;
    case 'subscription':
      return <i className="fa-solid fa-gift"></i>;
    case 'purchase_credits':
      return <i className="fa-solid fa-coins"></i>;
    case 'credits':
      return <i className="fa-solid fa-phone"></i>;
    case 'incoming_call_video':
      return <i className="fa-solid fa-video"></i>;
    case 'incoming_call_voice':
      return <i className="fa-solid fa-phone"></i>;
    case 'refund':
      return <i className="fa-solid fa-rotate-left"></i>;
    case 'discount':
    case 'discounted':
      return <i className="fa-solid fa-tags"></i>;
    case 'feed_content_sold':
    case 'message_content_sold':
    case 'feed_bundle_sold':
    case 'message_bundle_sold':
      return <i className="fa-solid fa-unlock"></i>;
    case 'new_subscribers':
    case 'new_followers':
      return <i className="fa-solid fa-users"></i>;
    case 'free_trial':
      return <i className="fa-solid fa-gift"></i>;
    case 'promo_trial_renewal':
      return <i className="fa-solid fa-gift"></i>;
    case 'promo_discount_renewal':
      return <i className="fa-solid fa-tags"></i>;
    case 'promo_discount_resubscribe':
      return <i className="fa-solid fa-tags"></i>;
    case 'promo_trial_resubscribe':
      return <i className="fa-solid fa-gift"></i>;
    case 'priority_messages':
      return <i className="fa-solid fa-star"></i>;
    case 'trending':
      return <i className="fa-solid fa-fire"></i>;
    default:
      return <i className="fa-solid fa-gift"></i>;
  }
}

export function parseDaysLeft(daysLeft) {
  if (daysLeft == null || daysLeft === '') return null;
  const num = Number(daysLeft);
  return Number.isFinite(num) ? num : null;
}

/**
 * Render occurrences of `@{username}` inside a text as styled username tokens.
 * @param {string} text
 * @param {string | null | undefined} username Username without leading '@'
 * @param {((username: string) => void) | undefined} onUsernameClick
 */
export function linkifyActivityUsername(text, username, onUsernameClick) {
  if (
    !text ||
    typeof text !== 'string' ||
    !username ||
    typeof username !== 'string'
  )
    return text;

  const cleanUsername = username.replace(/^@/, '');
  if (!cleanUsername) return text;

  // Match the exact "@username" token (case-insensitive) and keep original casing in the link text.
  const pattern = new RegExp(`@${escapeRegExp(cleanUsername)}\\b`, 'gi');

  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    const start = match.index;
    const matchedText = match[0]; // includes '@'
    const end = start + matchedText.length;

    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    parts.push(
      <span
        // start index is stable per render for a given text
        key={`u-${start}-${matchedText}`}
        className={`activity_username ${onUsernameClick ? 'cursor-pointer' : ''}`}
        onClick={onUsernameClick}
      >
        {matchedText}
      </span>
    );

    lastIndex = end;
  }

  if (parts.length === 0) return text;
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));

  return parts;
}

/**
 * Whether to render the description line below the heading.
 * Grouped APIs often send the same string as title and message; when the heading
 * already shows that title, skip the redundant body. When the heading is only
 * `@username`, the body still carries the story — keep it.
 */
export function shouldShowActivityDescription(
  item,
  sectionTitle,
  { isPriorityTab = false } = {}
) {
  const desc = String(item?.description || item?.message || '').trim();
  if (!desc) return false;
  if (isPriorityTab) return true;

  const tit = String(item?.title || '').trim();

  if (TITLES_WITH_INLINE_MENTIONS.has(sectionTitle)) {
    return tit !== desc;
  }
  if (item.username && sectionTitle !== ACTIVITY_TITLE_MAP.tags) {
    return true;
  }
  return tit !== desc;
}

/** Renders body text with `@username` tokens linked when applicable. */
export function renderActivityMessage(item, onUsernameClick) {
  const activityText = item?.description || item?.message || '';
  const username = item?.username;
  return linkifyActivityUsername(activityText, username, onUsernameClick) || '';
}

/**
 * Render @mentions in activity title with username styling.
 * Example: "Discount with @Sessy" -> "Discount with " + styled "@Sessy"
 */
export function renderTitleWithMentions(titleText, onMentionClick) {
  if (!titleText || typeof titleText !== 'string') return titleText;

  const mentionPattern = /@[A-Za-z0-9._-]+\b/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionPattern.exec(titleText)) !== null) {
    const start = match.index;
    const mention = match[0];
    const end = start + mention.length;

    if (start > lastIndex) {
      parts.push(titleText.slice(lastIndex, start));
    }

    parts.push(
      <span
        key={`t-${start}-${mention}`}
        className={`activity_username ${onMentionClick ? 'cursor-pointer' : ''}`}
        onClick={onMentionClick}
      >
        {mention}
      </span>
    );

    lastIndex = end;
  }

  if (parts.length === 0) return titleText;
  if (lastIndex < titleText.length) parts.push(titleText.slice(lastIndex));

  return parts;
}

export function renderPillText(pillText, amountClassName) {
  if (!pillText || typeof pillText !== 'string') return pillText;
  // Capture dollar amount separately so we can format the numeric part.
  // Example: "Received $14.999" -> before="Received " amount="$14.999" after=""
  const match = pillText.match(/^(.*?)(\$\d+(?:\.\d+)?)(.*)$/);
  if (!match) return pillText;

  const [, before, amount, after] = match;
  const amountWithoutDollar = String(amount).replace(/^\$/, '');
  return (
    <>
      {before}
      <span className={amountClassName}>${formatUsdAmountDisplayCents(amountWithoutDollar)}</span>
      {after}
    </>
  );
}

function buildSalesTipsBadgeRenderers(userRole) {
  const tipLead = userRole !== ROLE.USER ? 'Received ' : 'Sent ';
  const fmt = formatUsdAmountDisplayCents;
  return {
    tip: (amt) => (
      <>
        {tipLead} <span>${fmt(amt)}</span> tip
      </>
    ),
    message_tip: (amt) => (
      <>
        {tipLead} <span>${fmt(amt)}</span> tip
      </>
    ),
    ppv: (amt) => (
      <>
        PPV sold for <span>${fmt(amt)}</span>
      </>
    ),
    unlock: (amt) => (
      <>
        Content sold for <span>${fmt(amt)}</span>
      </>
    ),
    bundle: (amt) => (
      <>
        Bundle sold for <span>${fmt(amt)}</span>
      </>
    ),
    bundles: (amt) => (
      <>
        Bundles sold for <span>${fmt(amt)}</span>
      </>
    ),
    subscription: (amt) => (
      <>
        {userRole !== ROLE.USER
          ? 'Subscription received for '
          : 'Subscribed for '}
        <span>${fmt(amt)}</span>
      </>
    ),
    credits: (amt) => (
      <>
        Call Credits — <span>${fmt(amt)}</span>
      </>
    ),
    refund: (amt) => (
      <>
        Refunded of <span>${fmt(amt)}</span>
      </>
    ),
  };
}

export function getActivityBadge(title, item, userRole) {
  if (title === ACTIVITY_TITLE_MAP.subscription && item.type === 'subscription') {
    return null;
  }

  if (title === ACTIVITY_TITLE_MAP.purchases_tips && item.type !== 'refund') {
    return null;
  }

  // Priority messages — sentTip + daysLeft (never for refunds)
  const daysLeftNum = parseDaysLeft(item.daysLeft);
  if (
    item.type !== 'refund' &&
    item.sentTip != null &&
    Number.isFinite(daysLeftNum) &&
    daysLeftNum >= 0
  ) {
    return (
      <div className="d-flex align-items-center gap-2 mt-2 activity_badge_wrap">
        <span className="activity_pink_badge">
          {userRole !== ROLE.USER ? 'Received ' : 'Sent '}{' '}
          <span>${formatUsdAmountDisplayCents(item.sentTip)}</span>
          {userRole === ROLE.USER ? ' tip' : ''}
        </span>
        <span
          className={`response_time ${daysLeftNum > 3 ? 'text-muted' : 'text_danger'
            }`}
        >
          {userRole !== ROLE.USER ? 'You have' : 'Creator has'}{' '}
          {daysLeftNum} day{daysLeftNum === 1 ? '' : 's'} to respond
        </span>
      </div>
    );
  }

  // Payout badges — processed / pending / failed
  const payoutBadgeMeta = {
    payout_processed: {
      label: 'Processed',
      className: 'activity_pink_badge',
      wrapClassName: 'mt-2',
    },
    payout_pending: {
      label: 'Initiated',
      className: 'activity_badge_pending',
      wrapClassName: 'mt-1',
    },
    payout_failed: {
      label: 'Failed',
      className: 'activity_badge_failed',
      wrapClassName: 'mt-1',
    },
  }[item.type];

  if (payoutBadgeMeta) {
    const fallbackBadge =
      item.amount != null ? (
        <>
          {payoutBadgeMeta.label}{' '}
          <span className="badge_amount">
            ${formatUsdAmountDisplayCents(item.amount)}
          </span>
        </>
      ) : null;
    const badgeContent = item.pillText
      ? renderPillText(item.pillText, 'badge_amount')
      : fallbackBadge;

    if (!badgeContent) return null;

    return (
      <div className={payoutBadgeMeta.wrapClassName}>
        <span className={payoutBadgeMeta.className}>
          {badgeContent}
        </span>
      </div>
    );
  }

  if (item.pillText) {
    // New Posts: ActivityList renders `ppv_badge` for PPV rows, so skip the API
    // pillText (e.g. "Total $48") and show the lock + "PPV content" price line.
    const skipPillForNewPostsPpv =
      title === ACTIVITY_TITLE_MAP.new_posts &&
      item.isPPV &&
      item.price != null &&
      Number(item.price) > 0;
    if (skipPillForNewPostsPpv) {
      return null;
    }
    return (
      <div className="mt-2">
        <span className="activity_pink_badge">{renderPillText(item.pillText)}</span>
      </div>
    );
  }

  // Subscriber promotion: expired resubscribe — dollar amount or API `discounted_price: "free"`
  const showPromoResubscribeCta =
    (item.type === 'promo_discount_resubscribe' ||
      item.type === 'promo_trial_resubscribe') &&
    (item.promoResubscribeIsFree ||
      (item.amount != null && Number.isFinite(Number(item.amount))));
  if (showPromoResubscribeCta) {
    return (
      <div className="mt-2">
        <span className="activity_promotion_resubscribe_cta">
          Resubscribe for{' '}
          {item.promoResubscribeIsFree ? (
            <span className="badge_amount">Free</span>
          ) : (
            <span className="badge_amount">
              ${formatUsdAmountDisplayCents(item.amount)}
            </span>
          )}
        </span>
      </div>
    );
  }

  // Promotion Campaign badges
  if (
    item.type === 'discounted' &&
    item.amount != null &&
    Number.isFinite(Number(item.amount))
  ) {
    return (
      <div className="mt-2">
        <span className="activity_pink_badge">
          Discounted to{' '}
          <span className="badge_amount">
            ${formatUsdAmountDisplayCents(item.amount)}
          </span>
        </span>
      </div>
    );
  }
  if (item.type === 'free_trial') {
    return (
      <div className="mt-2">
        <span className="activity_pink_badge">
          Offer ends in {item.offerDays} days
        </span>
      </div>
    );
  }

  const salesTipsBadges = buildSalesTipsBadgeRenderers(userRole);
  const labelFn = salesTipsBadges[item.type];
  if (labelFn && item.amount != null && Number.isFinite(Number(item.amount))) {
    return (
      <div className="mt-2">
        <span className="activity_pink_badge">{labelFn(item.amount)}</span>
      </div>
    );
  }

  return null;
}
